// ============================================================
// TMR Portal Admin — Contingência (rota /contingencia) (31 §9)
// ============================================================
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@tmr/api-client';
import { PageHeader } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Notice } from '../../components/Notice';
import { Btn, Segmented } from '../../components/primitives';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useContingencyTypes, useUpdateContingencySetting } from '../../data/contingency/queries';
import type { ContingencyType } from '../../data/contingency/contract';
import { contingencyTypeCopy, formatMoment, pendingLabel, typeSituation } from '../../data/contingency/status';

/**
 * The panel: one card per workaround the TMR's support can use, with how a manager set it up. Whoever holds
 * `contingency.manage` switches each one on or off and decides whether it needs approval; everybody with the panel
 * opens a kind's page from its card.
 */
export function ContingencyPage() {
  const canAccess = usePermission(PERMISSIONS.contingencyAccess);
  const query = useContingencyTypes(canAccess);

  if (!canAccess) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState message="Você não tem permissão para ver a Contingência." hint="Peça o acesso a quem administra os papéis." />
        </div>
      </div>
    );
  }

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Contingência"
        title="Contingência"
        desc="Os desvios que o suporte da TMR usa quando o caminho normal falha para um paciente. Cada uso fica registrado com quem pediu, para quem, por quê e quem aprovou."
      />

      {query.isPending ? (
        <Skeleton rows={3} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.items.length === 0 ? (
        <div className="pa-card">
          <EmptyState message="Nenhuma contingência disponível." />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {query.data.items.map((type) => (
            <ContingencyCard key={type.key} type={type} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A switch change waiting for the manager's confirmation. */
interface PendingChange {
  enabled: boolean;
  requiresApproval: boolean;
  title: string;
  message: string;
}

/**
 * One kind: what it does, where it stands, and — for the manager — its two switches. Every switch asks for
 * confirmation, because each one changes what the whole support team can do from the next request on.
 */
function ContingencyCard({ type }: { type: ContingencyType }) {
  const canManage = usePermission(PERMISSIONS.contingencyManage);
  const update = useUpdateContingencySetting();
  const [pending, setPending] = useState<PendingChange | null>(null);
  const copy = contingencyTypeCopy(type.key);
  const situation = typeSituation(type);

  const ask = (change: Omit<PendingChange, 'title' | 'message'>) => {
    if (change.enabled !== type.enabled) {
      setPending({
        ...change,
        title: change.enabled ? `Ligar ${copy.name.toLowerCase()}?` : `Desligar ${copy.name.toLowerCase()}?`,
        message: change.enabled
          ? 'Quem tem a permissão volta a poder usar esta contingência a partir de agora.'
          : 'Ninguém mais consegue pedir nem gerar, nem os pedidos já aprovados. Os pedidos e o histórico ficam guardados.',
      });
      return;
    }
    setPending({
      ...change,
      title: change.requiresApproval ? 'Exigir aprovação?' : 'Dispensar aprovação?',
      message: change.requiresApproval
        ? 'Cada pedido novo passa a esperar um gestor de Contingência, que não pode ser quem pediu.'
        : 'Os pedidos novos passam a ser atendidos na hora. Os que já aguardam continuam esperando a decisão.',
    });
  };

  return (
    <div className="pa-card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 19, margin: 0, color: 'var(--ink-900)' }}>{copy.name}</h2>
        <p style={{ margin: '6px 0 0', fontSize: 13.5, lineHeight: 1.55, color: 'var(--fg-muted)' }}>{copy.description}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13.5 }}>
        <span
          style={{
            fontWeight: 600,
            color: situation.tone === 'success' ? 'var(--success)' : situation.tone === 'warning' ? 'var(--warning)' : 'var(--fg-muted)',
          }}
        >
          {situation.label}
        </span>
        <span style={{ color: 'var(--fg-muted)' }}>{pendingLabel(type.pendingCount)}</span>
        {type.updatedBy && type.updatedAt && (
          <span style={{ color: 'var(--fg-subtle)', fontSize: 12.5 }}>
            Configurado por {type.updatedBy.name} em {formatMoment(type.updatedAt)}
          </span>
        )}
      </div>

      {canManage && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 12, borderTop: '1px solid var(--border-default)' }}>
          <SwitchRow label="Uso">
            <Segmented
              size="sm"
              options={[
                { value: true, label: 'Ligado' },
                { value: false, label: 'Desligado' },
              ]}
              value={type.enabled}
              onChange={(enabled) => ask({ enabled, requiresApproval: type.requiresApproval })}
            />
          </SwitchRow>
          <SwitchRow label="Aprovação">
            <Segmented
              size="sm"
              options={[
                { value: false, label: 'Não exige' },
                { value: true, label: 'Exige aprovação' },
              ]}
              value={type.requiresApproval}
              onChange={(requiresApproval) => ask({ enabled: type.enabled, requiresApproval })}
            />
          </SwitchRow>
        </div>
      )}

      {update.isError && (
        <Notice tone="danger">
          {update.error instanceof ApiError ? update.error.detail : 'Não foi possível salvar a configuração.'}
        </Notice>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Link to={copy.path} style={{ textDecoration: 'none' }}>
          <Btn variant="secondary" icon={Icons.arrowR}>
            Abrir {copy.name.toLowerCase()}
          </Btn>
        </Link>
      </div>

      {pending && (
        <ConfirmDialog
          title={pending.title}
          message={pending.message}
          confirmLabel="Confirmar"
          danger={!pending.enabled}
          onConfirm={() =>
            update.mutateAsync({ key: type.key, enabled: pending.enabled, requiresApproval: pending.requiresApproval }).catch(() => undefined)
          }
          onClose={() => setPending(null)}
        />
      )}
    </div>
  );
}

/** A switch with its label on the left, so the two rows line up. */
function SwitchRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <span className="pa-eyebrow">{label}</span>
      {children}
    </div>
  );
}
